/**
 * Modelo do painel de sincronização (PG-063) — FUNÇÕES PURAS.
 * Reúne: linha de status (PG-062), 5 passos, estimativas do servidor e progresso do
 * download. Os passos 4 e 5 são ESTIMATIVAS: o front não recebe confirmação do servidor
 * (sem endpoint de status) — nunca escrever "confirmado". G018: o loop automático só ENVIA;
 * baixar exige Sincronizar — nenhum texto promete "baixando automaticamente".
 */
import type { PendingSummary } from './pendingData'
import { SYNC_STATUS } from '../redux/sync/constants'
import {
  deriveSyncStatusLine,
  formatAgo,
  pendingCount,
  relevantUnreadable,
  type StatusLine,
} from './syncStatusLine'
import {
  ESTIMATES_MEASURED_AT,
  PROCESSING_ASSUME_DONE_MIN,
  PROCESSING_PEAK_MIN,
  PROCESSING_TYPICAL_MIN,
  SERVER_PAUSE_BRT,
  SYNC_AUTO_PUSH_SECONDS,
  isServerPaused,
  serverPausedMinutesBetween,
} from './syncTimingEstimates'

export type StepId = 'saved' | 'queued' | 'sent' | 'processing' | 'available'
export type StepStatus =
  'done' | 'active' | 'waiting' | 'estimate' | 'attention' | 'unknown'
export type PanelStep = {
  id: StepId
  number: number
  title: string
  status: StepStatus
  detail: string
}

export type SyncOperation = { kind: 'sync' | 'download'; startedAt: number }
export type SyncProgressState = {
  attempt?: number | null
  maxAttempts?: number | null
  entity?: string | null
  entityIndex?: number | null
  totalEntities?: number | null
  recordsProcessed?: number | null
  totalRecords?: number | null
  phase?: 'processing' | 'chunk' | null
  chunkIndex?: number | null
  totalChunks?: number | null
  startTime?: number | null
}
export type PullSummary = {
  at: number
  farmsCount: number
  durationMs: number
  responseSizeKb: number
  userInitiated?: boolean
}
export type SyncNoticeState = {
  id: number
  severity: 'error' | 'warning' | 'info' | 'success'
  text: string
}

export type ProgressPhase = 'sending' | 'downloading' | 'applying' | 'retrying'
export type ProgressView = {
  phase: ProgressPhase
  title: string
  elapsedLabel: string | null
  entityLabel: string | null
  recordsLabel: string | null
  fraction: number | null
  chunkLabel: string | null
  retryLabel: string | null
  hint: string | null
  keepOpenWarning: string
}

export type SyncPanelInput = {
  now: number
  online: boolean
  summary: PendingSummary | null
  lastPushOkAt: number | null
  lastPullAt: number | null
  currentSync: number
  operation: SyncOperation | null
  progress: SyncProgressState | null
  lastPull: PullSummary | null
  notice: SyncNoticeState | null
}

export type PanelModel = {
  mode: 'panel' | 'progress'
  line: StatusLine
  steps: PanelStep[]
  progress: ProgressView | null
  notice: { severity: SyncNoticeState['severity']; text: string } | null
  refusedWarning: string | null
  lastPullText: string | null
  footnote: string
}

const MIN = 60 * 1000
/** Canal oficial de suporte (confirmado pelo Filipe). Treinamentos usa o mesmo número. */
export const SUPPORT_WHATSAPP_LABEL = '(11) 94311-0553'
export const SUPPORT_WHATSAPP_URL = 'https://wa.me/5511943110553'

/**
 * Aviso do painel para registros recusados pelo servidor (QA HML U4). Texto único aqui.
 * Verdade do motor: o push relê todo registro não sincronizado a cada ciclo e reenvia,
 * e o servidor recusa de novo — por isso NÃO dizer que o app "não tenta de novo".
 */
export function buildRefusedWarning(n: number): string {
  const one = n === 1
  return (
    `O servidor recusou ${n} ${one ? 'registro' : 'registros'}. ` +
    `${one ? 'Ele continua salvo' : 'Eles continuam salvos'} neste aparelho e ` +
    `${one ? 'vai' : 'vão'} no arquivo de Backup. ` +
    `O app continua tentando enviar, mas o servidor recusa. ` +
    `Fale com o suporte pelo WhatsApp ${SUPPORT_WHATSAPP_LABEL}.`
  )
}
const pad2 = (n: number) => String(n).padStart(2, '0')
const hourLabel = (h: number) => `${pad2(h)}h`
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** 12340 => "12.340" (determinístico, independe de ICU). */
export const formatInt = (n: number): string =>
  String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  if (total < 60) return `${total} s`
  return `${Math.floor(total / 60)} min ${pad2(total % 60)} s`
}

export function formatSizeKb(kb: number): string {
  return kb < 1024
    ? `${Math.round(kb)} KB`
    : `${(kb / 1024).toFixed(1).replace('.', ',')} MB`
}

export function describePullSummary(
  p: Pick<PullSummary, 'farmsCount' | 'durationMs' | 'responseSizeKb'>,
): string {
  return `Sincronização concluída: ${p.farmsCount} ${plural(p.farmsCount, 'fazenda', 'fazendas')} em ${formatElapsed(p.durationMs)} (${formatSizeKb(p.responseSizeKb)}).`
}

/** Nomes amigáveis das entidades do pull (chaves de `values` em pullChangesEnhanced). */
export const ENTITY_LABELS: Record<string, string> = {
  usuarios: 'usuários',
  tipoDominios: 'tipos de domínio',
  valorDominios: 'valores de domínio',
  fazendas: 'fazendas',
  retiros: 'retiros',
  protocolos: 'protocolos',
  lotes: 'lotes',
  d0s: 'D0',
  dns: 'DN',
  iatfs: 'IATFs',
  fazendaUsuarios: 'vínculos de usuários',
  touros: 'touros',
  partidas: 'partidas',
}

export function isSyncProgressMode(sync: {
  operation?: SyncOperation | null
  currentSync?: number
}): boolean {
  return (
    !!sync.operation ||
    sync.currentSync === SYNC_STATUS.DOWNLOADING_FARM ||
    sync.currentSync === SYNC_STATUS.PROCESSING ||
    sync.currentSync === SYNC_STATUS.RETRYING
  )
}

const PROGRESS_TITLES: Record<ProgressPhase, string> = {
  sending: 'Enviando suas alterações ao servidor…',
  downloading: 'Baixando os dados do servidor…',
  applying: 'Gravando os dados neste aparelho…',
  retrying: 'Houve uma falha. Tentando de novo…',
}

export function deriveProgressView(
  input: Pick<SyncPanelInput, 'now' | 'currentSync' | 'operation' | 'progress'>,
): ProgressView | null {
  const { now, currentSync, operation, progress } = input
  if (!isSyncProgressMode({ operation, currentSync })) return null

  const phase: ProgressPhase =
    currentSync === SYNC_STATUS.PROCESSING
      ? 'applying'
      : currentSync === SYNC_STATUS.DOWNLOADING_FARM
        ? 'downloading'
        : currentSync === SYNC_STATUS.RETRYING
          ? 'retrying'
          : 'sending'

  const startedAt = operation?.startedAt ?? progress?.startTime ?? null
  const total = progress?.totalRecords ?? 0
  const done = progress?.recordsProcessed ?? 0
  const applying = phase === 'applying'
  const entity = applying ? (progress?.entity ?? null) : null

  return {
    phase,
    title: PROGRESS_TITLES[phase],
    elapsedLabel: startedAt != null ? formatElapsed(now - startedAt) : null,
    entityLabel: entity ? (ENTITY_LABELS[entity] ?? entity) : null,
    recordsLabel:
      applying && total > 0
        ? `${formatInt(done)} de ${formatInt(total)} registros`
        : null,
    fraction: applying && total > 0 ? Math.min(1, done / total) : null,
    chunkLabel:
      applying &&
      progress?.phase === 'chunk' &&
      progress.chunkIndex &&
      progress.totalChunks
        ? `parte ${progress.chunkIndex} de ${progress.totalChunks}`
        : null,
    retryLabel:
      phase === 'retrying' && progress?.attempt
        ? `tentativa ${progress.attempt} de ${progress.maxAttempts || 3}`
        : null,
    hint:
      phase === 'downloading'
        ? 'Sem barra de progresso nesta etapa: o servidor envia tudo de uma vez.'
        : null,
    keepOpenWarning: 'Não feche esta aba até terminar.',
  }
}

function describeProcessing(now: number, lastPushOkAt: number): string {
  const [lo, hi] = PROCESSING_TYPICAL_MIN
  if (isServerPaused(now)) {
    return `O servidor não processa das ${hourLabel(SERVER_PAUSE_BRT.startHour)} às ${hourLabel(SERVER_PAUSE_BRT.endHour)}. O que você enviou será processado a partir das ${hourLabel(SERVER_PAUSE_BRT.endHour)}.`
  }
  const effectiveMin =
    (now - lastPushOkAt) / MIN - serverPausedMinutesBetween(lastPushOkAt, now)
  if (effectiveMin >= PROCESSING_ASSUME_DONE_MIN) {
    return 'Já deve ter sido processado (estimativa).'
  }
  if (effectiveMin > PROCESSING_PEAK_MIN) {
    return `Está levando mais que o normal. No pico pode passar de ${PROCESSING_PEAK_MIN} min — isso não é erro.`
  }
  return `Em processamento no servidor — normalmente ${lo}–${hi} min.`
}

export function deriveSyncPanelModel(input: SyncPanelInput): PanelModel {
  const { now, online, summary, lastPushOkAt, lastPullAt } = input
  const line = deriveSyncStatusLine({ now, online, summary, lastPushOkAt })
  const pending = summary ? pendingCount(summary) : 0
  // legível = temos summary e (há pendência conhecida OU nenhuma fonte relevante falhou)
  const readable =
    !!summary && (pending > 0 || relevantUnreadable(summary).length === 0)
  const unknown = 'Não foi possível verificar.'

  const queued: PanelStep = !readable
    ? {
        id: 'queued',
        number: 2,
        title: 'Aguardando envio',
        status: 'unknown',
        detail: unknown,
      }
    : pending > 0
      ? {
          id: 'queued',
          number: 2,
          title: 'Aguardando envio',
          status: 'active',
          detail:
            `${pending} ${plural(pending, 'registro aguarda', 'registros aguardam')} envio. O app envia sozinho a cada ${SYNC_AUTO_PUSH_SECONDS} s.` +
            (online
              ? ''
              : ' Sem internet agora: o envio retoma quando a conexão voltar.'),
        }
      : {
          id: 'queued',
          number: 2,
          title: 'Aguardando envio',
          status: 'done',
          detail: 'Nada aguardando envio.',
        }

  const refused = summary?.deadLetter ?? 0
  const nothingSentYet = readable && pending === 0 && lastPushOkAt == null
  const sent: PanelStep = !readable
    ? {
        id: 'sent',
        number: 3,
        title: 'Enviado (na fila do servidor)',
        status: 'unknown',
        detail: unknown,
      }
    : pending > 0
      ? {
          id: 'sent',
          number: 3,
          title: 'Enviado (na fila do servidor)',
          status: 'waiting',
          detail: 'Ainda não enviado.',
        }
      : refused > 0
        ? {
            id: 'sent',
            number: 3,
            title: 'Enviado (na fila do servidor)',
            status: 'attention',
            detail:
              lastPushOkAt != null
                ? `Enviado em parte. O servidor aceitou o último envio ${formatAgo(now, lastPushOkAt)}, mas recusou ${refused} ${plural(refused, 'registro', 'registros')} (veja o aviso acima).`
                : `Nenhum envio foi aceito pelo servidor ainda e há ${refused} ${plural(refused, 'registro recusado', 'registros recusados')} (veja o aviso acima).`,
          }
        : nothingSentYet
          ? {
              id: 'sent',
              number: 3,
              title: 'Enviado (na fila do servidor)',
              status: 'waiting',
              detail: 'Nenhum envio registrado neste aparelho ainda.',
            }
          : {
              id: 'sent',
              number: 3,
              title: 'Enviado (na fila do servidor)',
              status: 'done',
              detail: `Último envio aceito pelo servidor ${formatAgo(now, lastPushOkAt as number)}. Ele entrou na fila de processamento.`,
            }

  const canEstimate = readable && pending === 0 && lastPushOkAt != null
  const processing: PanelStep = !readable
    ? {
        id: 'processing',
        number: 4,
        title: 'Processado no servidor (estimativa)',
        status: 'unknown',
        detail: unknown,
      }
    : canEstimate
      ? {
          id: 'processing',
          number: 4,
          title: 'Processado no servidor (estimativa)',
          status: 'estimate',
          detail: describeProcessing(now, lastPushOkAt as number),
        }
      : {
          id: 'processing',
          number: 4,
          title: 'Processado no servidor (estimativa)',
          status: 'waiting',
          detail: 'Começa quando o servidor receber o envio.',
        }

  const pulledAfterPeak =
    canEstimate &&
    lastPullAt != null &&
    lastPullAt >= (lastPushOkAt as number) + PROCESSING_PEAK_MIN * MIN
  const available: PanelStep = !readable
    ? {
        id: 'available',
        number: 5,
        title: 'Disponível nos outros aparelhos (estimativa)',
        status: 'unknown',
        detail: unknown,
      }
    : pulledAfterPeak
      ? {
          id: 'available',
          number: 5,
          title: 'Disponível nos outros aparelhos (estimativa)',
          status: 'done',
          detail: `Este aparelho baixou os dados do servidor ${formatAgo(now, lastPullAt as number)}, depois do prazo normal de processamento. Nos outros aparelhos, toque em Sincronizar para ver.`,
        }
      : {
          id: 'available',
          number: 5,
          title: 'Disponível nos outros aparelhos (estimativa)',
          status: 'waiting',
          detail:
            'Depois do passo 4, toque em Sincronizar (aqui ou no outro aparelho) para baixar. O app não baixa sozinho.',
        }

  const [d, m, y] = [
    ESTIMATES_MEASURED_AT.slice(8, 10),
    ESTIMATES_MEASURED_AT.slice(5, 7),
    ESTIMATES_MEASURED_AT.slice(0, 4),
  ]
  const progress = deriveProgressView(input)

  return {
    mode: progress ? 'progress' : 'panel',
    line,
    steps: [
      {
        id: 'saved',
        number: 1,
        title: 'Salvo neste aparelho',
        status: 'done',
        detail: 'Seus registros ficam guardados aqui, mesmo sem internet.',
      },
      queued,
      sent,
      processing,
      available,
    ],
    progress,
    notice: input.notice
      ? { severity: input.notice.severity, text: input.notice.text }
      : null,
    refusedWarning: refused > 0 ? buildRefusedWarning(refused) : null,
    lastPullText: input.lastPull
      ? `Último download: ${input.lastPull.farmsCount} ${plural(input.lastPull.farmsCount, 'fazenda', 'fazendas')} · ${formatElapsed(input.lastPull.durationMs)} · ${formatSizeKb(input.lastPull.responseSizeKb)} · ${formatAgo(now, input.lastPull.at)}`
      : null,
    footnote: `Os passos 4 e 5 são estimativas: o servidor não avisa quando termina. Tempos medidos em ${d}/${m}/${y} (últimos 14 dias).`,
  }
}
